<template>
  <AppButton
    v-if="withLocalModalTrigger"
    v-tippy="{
      content: $t('menu.support'),
      placement: 'right'
    }"
    class="sm-Trigger"
    height="24"
    icon="menu-support"
    remove-padding
    size="sm"
    type="ghost-next"
    width="24"
    @click="onOpenModal"
  />

  <portal to="modal-windows">
    <Modal
      :scrollable-content="false"
      :show="resolvedShow"
      :size="600"
      :title="$t('menu.support')"
      class="sm-Modal"
      manual-close
      sticky-header
      @close="onClose"
    >
      <template v-if="isPluginAdmin && !hideDebugButton" #before-close>
        <AppButton
          size="sm"
          type="subtle"
          @click="goToDebug"
          @mouseleave="changePlayState(false)"
          @mouseover="changePlayState(true)"
        >
          <AppRiveScene
            ref="riveScene"
            file-src="https://s3-oboard-public-static.s3.eu-central-1.amazonaws.com/img/debug.riv"
            height="24"
            width="24"
          >
            <template #fallback>
              <AppIcon height="24" icon-name="bug" width="24" />
            </template>
          </AppRiveScene>
        </AppButton>
      </template>

      <SupportModalContent />
    </Modal>
  </portal>
</template>

<script setup>
import { computed, ref, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { ROUTE_NAMES } from '@/routes/route-helpers'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'

import SupportModalContent from '@/components/support/SupportModalContent'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppRiveScene from '@/components/ui/AppRiveScene/AppRiveScene'
import Modal from '@/components/ui/Modal/Modal'

defineOptions({
  name: 'SupportModal'
})

const props = defineProps({
  show: {
    type: Boolean
  },

  withLocalModalTrigger: {
    type: Boolean
  },

  hideDebugButton: {
    type: Boolean
  }
})

const emit = defineEmits({
  'update:show': null
})

const onUpdateShow = (value = false) => {
  emit('update:show', value)
}

const onClose = () => {
  if (props.withLocalModalTrigger) {
    localShow.value = false
  } else {
    onUpdateShow()
  }
}

const localShow = ref(false)

const resolvedShow = computed(() => {
  return props.withLocalModalTrigger ? localShow.value : props.show
})

const onOpenModal = () => {
  if (props.withLocalModalTrigger) {
    localShow.value = true
  } else {
    onUpdateShow(true)
  }
}

const store = useStore()

const isPluginAdmin = computed(() => store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.IS_PLUGIN_ADMIN])

const router = useRouter()

const goToDebug = () => {
  onClose()
  router.push({ name: ROUTE_NAMES.DEBUG })
}

const riveSceneReference = useTemplateRef('riveScene')

const changePlayState = state => {
  if (!riveSceneReference.value) return

  if (state) {
    riveSceneReference.value.play()
  } else {
    riveSceneReference.value.reset()
  }
}
</script>

<style lang="scss" scoped>
.sm-Modal {
  --modal-header-padding: 14px #{$page-right-padding} 0 #{$page-left-padding};
  --modal-title-margin: 0 0 18px 0;
  --modal-body-padding: 0 #{$page-right-padding} #{$page-bottom-padding} #{$page-left-padding};
}

.sm-Trigger {
  position: relative;
  width: 100%;
  justify-content: flex-start;
  min-height: 40px;
  padding: 8px;
  gap: 8px;
  border-radius: $border-radius-md-next;

  color: $dark-2;

  &:hover {
    color: $primary-color-next;
  }
}
</style>
